import { type LooseObject, P } from '@piccolohealth/util';

export type PiccoloErrorType =
  | 'AiCacheCreationError'
  | 'AiResourceExhausted'
  | 'AiResponseParseError'
  | 'AuthInvalidChangePasswordCode'
  | 'AuthInvalidUnenrolMfaCode'
  | 'AuthInvalidVerifyInviteCode'
  | 'AuthUnableToGenerateVerifyInviteCode'
  | 'AuthUnableToGenerateChangePasswordCode'
  | 'AuthMfaRequiredForOrganization'
  | 'AuthUnableToGenerateResetMfaCode'
  | 'AuthUnableToVerifyToken'
  | 'AuthUserAlreadyVerified'
  | 'BillingActionNotFound'
  | 'ContentTooLarge'
  | 'DocumentNotFound'
  | 'FileEmpty'
  | 'Forbidden'
  | 'FormDecodeResultUnprocessable'
  | 'FormNotFound'
  | 'IncidentNotFound'
  | 'IncidentCategoryNotFound'
  | 'IncidentFileNotFound'
  | 'InternalClientError'
  | 'InternalServerError'
  | 'JobCreationError'
  | 'LabelNotFound'
  | 'OrganizationNotFound'
  | 'OrganizationSuspended'
  | 'ParticipantBehaviourMethodNotFound'
  | 'ParticipantBehaviourMethodNotActive'
  | 'ParticipantBehaviourNotActive'
  | 'ParticipantBehaviourNotFound'
  | 'ParticipantBehaviourRecordingUserNotFound'
  | 'ParticipantBehaviourRecordingUserDetailsNotFound'
  | 'ParticipantDocumentNotFound'
  | 'ParticipantDocumentFinalized'
  | 'ParticipantDocumentVersionNotFound'
  | 'ParticipantExportError'
  | 'IncidentExportError'
  | 'ParticipantKeyContactNotFound'
  | 'ParticipantFileDuplicateName'
  | 'ParticipantFileNotFound'
  | 'ParticipantFieldTemplateNotFound'
  | 'ParticipantFieldGroupNotFound'
  | 'ParticipantFieldGroupTemplateNotFound'
  | 'ParticipantNotActive'
  | 'ParticipantNotFound'
  | 'ParticipantNoteNotFound'
  | 'ParticipantNoteVersionNotFound'
  | 'ParticipantQrNotActive'
  | 'ParticipantQrNotFound'
  | 'ParticipantQrBehaviourNotFound'
  | 'ParticipantRestrictivePracticeNotFound'
  | 'ParticipantTaskNotFound'
  | 'ParticipantTaskTemplateNotFound'
  | 'ParticipantUserNotFound'
  | 'PostmarkRequestFailure'
  | 'RateLimitExceeded'
  | 'StorageUploadInvalidDataType'
  | 'TallyRequestFailure'
  | 'TemplateNotFound'
  | 'TemplateScoringChecklistNotFound'
  | 'TemplateVersionNotFound'
  | 'IncorrectTemplateType'
  | 'Unauthorized'
  | 'UserNotFound'
  | 'UserOrganizationNotFound';

export interface PiccoloErrorProps {
  type: PiccoloErrorType;
  message: string;
  metadata?: LooseObject;
  stack?: string;
}

export class PiccoloError extends Error {
  type: PiccoloErrorType;
  statusCode: number;
  message: string;
  metadata?: LooseObject;

  constructor(response: PiccoloErrorProps) {
    const { type, message, stack, metadata } = response;
    // Not super(message): that makes message non-enumerable, so JSON.stringify
    // drops it and the message never reaches the client or the logs
    super();

    this.message = message;
    this.name = type;
    this.type = type;
    this.statusCode = PiccoloError.getStatusCode(type);
    this.metadata = metadata;

    if (stack) {
      this.stack = stack;
    }
  }

  static getStatusCode(type: PiccoloErrorType): number {
    switch (type) {
      case 'AuthInvalidChangePasswordCode':
      case 'AuthInvalidVerifyInviteCode':
      case 'AuthInvalidUnenrolMfaCode':
      case 'AuthUserAlreadyVerified':
      case 'FileEmpty':
      case 'IncorrectTemplateType':
      case 'StorageUploadInvalidDataType':
        return 400;
      case 'AuthUnableToVerifyToken':
      case 'Unauthorized':
        return 401;
      case 'AuthMfaRequiredForOrganization':
      case 'Forbidden':
      case 'OrganizationSuspended':
      case 'ParticipantNotActive':
      case 'ParticipantBehaviourNotActive':
      case 'ParticipantBehaviourMethodNotActive':
      case 'ParticipantQrNotActive':
        return 403;
      case 'BillingActionNotFound':
      case 'DocumentNotFound':
      case 'FormNotFound':
      case 'IncidentNotFound':
      case 'IncidentCategoryNotFound':
      case 'IncidentFileNotFound':
      case 'LabelNotFound':
      case 'OrganizationNotFound':
      case 'ParticipantBehaviourMethodNotFound':
      case 'ParticipantBehaviourNotFound':
      case 'ParticipantBehaviourRecordingUserNotFound':
      case 'ParticipantBehaviourRecordingUserDetailsNotFound':
      case 'ParticipantDocumentNotFound':
      case 'ParticipantDocumentFinalized':
      case 'ParticipantDocumentVersionNotFound':
      case 'ParticipantFileNotFound':
      case 'ParticipantFieldTemplateNotFound':
      case 'ParticipantFieldGroupNotFound':
      case 'ParticipantFieldGroupTemplateNotFound':
      case 'ParticipantKeyContactNotFound':
      case 'ParticipantNotFound':
      case 'ParticipantNoteVersionNotFound':
      case 'ParticipantNoteNotFound':
      case 'ParticipantQrNotFound':
      case 'ParticipantQrBehaviourNotFound':
      case 'ParticipantRestrictivePracticeNotFound':
      case 'ParticipantTaskNotFound':
      case 'ParticipantTaskTemplateNotFound':
      case 'ParticipantUserNotFound':
      case 'TemplateNotFound':
      case 'TemplateScoringChecklistNotFound':
      case 'TemplateVersionNotFound':
      case 'UserNotFound':
      case 'UserOrganizationNotFound':
        return 404;
      case 'ParticipantFileDuplicateName':
        return 409;
      case 'ContentTooLarge':
        return 413;
      case 'FormDecodeResultUnprocessable':
        return 422;
      case 'AiResourceExhausted':
      case 'RateLimitExceeded':
        return 429;
      case 'AiCacheCreationError':
      case 'AiResponseParseError':
      case 'AuthUnableToGenerateChangePasswordCode':
      case 'AuthUnableToGenerateResetMfaCode':
      case 'AuthUnableToGenerateVerifyInviteCode':
      case 'PostmarkRequestFailure':
      case 'TallyRequestFailure':
      case 'ParticipantExportError':
      case 'IncidentExportError':
      case 'InternalClientError':
      case 'InternalServerError':
      case 'JobCreationError':
        return 500;
    }
  }
}

export const createClientError = (error: PiccoloError): PiccoloError => {
  const { message, metadata } = P.run((): { message: string; metadata?: LooseObject } => {
    switch (error.type) {
      case 'AuthUnableToVerifyToken':
      case 'Unauthorized':
        return { message: 'You are not authorized to access this resource' };

      case 'AuthUserAlreadyVerified':
        return { message: 'The user has already been verified' };

      case 'FileEmpty': {
        return { message: 'The file you are trying to upload is empty' };
      }

      case 'ContentTooLarge': {
        return { message: 'The content being submitted is too large' };
      }

      case 'Forbidden':
        return { message: 'You are not permitted to access this resource' };

      case 'AuthMfaRequiredForOrganization':
        return { message: 'You must have MFA enabled to access this organization' };

      case 'AuthInvalidChangePasswordCode':
        return { message: 'Could not change password, the link is invalid or has expired' };

      case 'AuthInvalidUnenrolMfaCode':
        return { message: 'Could not unenrol MFA, the link is invalid or has expired' };

      case 'AuthInvalidVerifyInviteCode':
        return { message: 'Could not verify invite, the link is invalid or has expired' };

      case 'TemplateNotFound':
      case 'TemplateVersionNotFound': {
        return { message: 'We could not find the template you were looking for' };
      }

      case 'TemplateScoringChecklistNotFound': {
        return { message: 'We could not find the template scoring checklist you were looking for' };
      }

      case 'FormNotFound': {
        return { message: 'We could not find the form you were looking for' };
      }

      case 'IncidentNotFound': {
        return { message: 'We could not find the incident you were looking for' };
      }

      case 'IncidentCategoryNotFound': {
        return { message: 'We could not find the incident category you were looking for' };
      }

      case 'LabelNotFound': {
        return { message: 'We could not find the label you were looking for' };
      }

      case 'OrganizationNotFound':
      case 'UserOrganizationNotFound': {
        return { message: 'We could not find the organization you were looking for' };
      }

      case 'OrganizationSuspended': {
        return {
          message: 'This organization has been suspended. Please contact support for assistance',
        };
      }

      case 'ParticipantNotFound': {
        return { message: 'We could not find the participant you were looking for' };
      }

      case 'IncidentFileNotFound':
      case 'ParticipantDocumentNotFound': {
        return { message: 'We could not find the document you were looking for' };
      }

      case 'ParticipantDocumentFinalized':
        return { message: 'This document has been finalized and can no longer be edited.' };

      case 'ParticipantDocumentVersionNotFound':
        return { message: 'We could not find the document version you were looking for' };

      case 'ParticipantFileNotFound': {
        return { message: 'We could not find the file or document you were looking for' };
      }

      case 'ParticipantFileDuplicateName': {
        return { message: 'The filename you provided already exists, please choose another.' };
      }

      case 'ParticipantFieldTemplateNotFound': {
        return { message: 'We could not find the participant field you were looking for' };
      }

      case 'ParticipantFieldGroupNotFound':
      case 'ParticipantFieldGroupTemplateNotFound': {
        return { message: 'We could not find the participant field group you were looking for' };
      }

      case 'ParticipantExportError':
      case 'IncidentExportError': {
        return { message: 'We could not export the data you were looking for' };
      }

      case 'ParticipantNoteVersionNotFound':
      case 'ParticipantNoteNotFound': {
        return { message: 'We could not find the note you were looking for' };
      }

      case 'ParticipantBehaviourNotFound': {
        return { message: 'We could not find the behaviour you were looking for' };
      }

      case 'ParticipantQrNotFound': {
        return { message: 'We could not find the QR code you were looking for' };
      }

      case 'ParticipantUserNotFound': {
        return { message: 'We could not find the team member you were looking for' };
      }

      case 'ParticipantKeyContactNotFound': {
        return { message: 'We could not find the key contact you were looking for' };
      }

      case 'UserNotFound': {
        return { message: 'We could not find the user you were looking for' };
      }

      case 'IncorrectTemplateType':
        return {
          message: 'The selected template type cannot be used to create a document with this type.',
        };

      case 'ParticipantNotActive': {
        return { message: 'This participant is not active and its actions have been disabled' };
      }

      case 'ParticipantBehaviourNotActive': {
        return { message: 'This behaviour is not active and its actions have been disabled' };
      }

      case 'ParticipantBehaviourMethodNotActive': {
        return {
          message: 'This behaviour method is not active and its actions have been disabled',
        };
      }

      case 'ParticipantBehaviourRecordingUserNotFound': {
        return { message: 'We could not find the user associated with this recording' };
      }

      case 'ParticipantBehaviourRecordingUserDetailsNotFound': {
        return { message: 'We could not find the user details associated with this recording' };
      }

      case 'ParticipantQrNotActive': {
        return { message: 'This QR code is not active and its actions have been disabled' };
      }

      case 'ParticipantRestrictivePracticeNotFound': {
        return { message: 'We could not find the restrictive practice you were looking for' };
      }

      case 'DocumentNotFound': {
        return { message: 'We could not find the document you were looking for' };
      }

      case 'ParticipantTaskNotFound': {
        return { message: 'We could not find the task you were looking for' };
      }

      case 'ParticipantTaskTemplateNotFound': {
        return { message: 'We could not find the task template you were looking for' };
      }

      case 'AiResourceExhausted':
        return { message: 'The AI service is currently busy. Please wait a moment and try again.' };

      case 'RateLimitExceeded': {
        return { message: 'You have exceeded the rate limit' };
      }

      case 'AiCacheCreationError':
      case 'AiResponseParseError':
      case 'AuthUnableToGenerateChangePasswordCode':
      case 'AuthUnableToGenerateResetMfaCode':
      case 'AuthUnableToGenerateVerifyInviteCode':
      case 'BillingActionNotFound':
      case 'FormDecodeResultUnprocessable':
      case 'InternalClientError':
      case 'InternalServerError':
      case 'JobCreationError':
      case 'ParticipantBehaviourMethodNotFound':
      case 'ParticipantQrBehaviourNotFound':
      case 'PostmarkRequestFailure':
      case 'StorageUploadInvalidDataType':
      case 'TallyRequestFailure':
        return {
          message: 'Something broke. Try again, and if the issue persists, please contact support.',
        };
    }
  });

  return new PiccoloError({ type: error.type, message, metadata });
};
